import {
  Alert,
  Anchor,
  Badge,
  Button,
  Center,
  Group,
  NativeSelect,
  Stack,
  Table,
  Text,
  Title,
} from '@mantine/core';
import { IconAlertTriangle, IconPlus } from '@tabler/icons-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  useQuestionSets,
  type ListQuestionSetsFilters,
  type QuestionSetStatus,
} from '../api/question-sets';
import { useOrgs } from '../api/orgs';
import { useProducts } from '../api/products';

const STATUS_COLOURS: Record<QuestionSetStatus, string> = {
  draft: 'yellow',
  active: 'green',
  archived: 'gray',
};

function statusBadge(status: QuestionSetStatus) {
  return (
    <Badge color={STATUS_COLOURS[status] ?? 'gray'} variant="light">
      {status}
    </Badge>
  );
}

function formatDate(iso: string | null): string {
  return iso ? new Date(iso).toLocaleString() : '—';
}

export function QuestionSetsListPage() {
  // Admin operators are cross-tenant by default — the list returns
  // sets across every org. Selectors narrow the view without typing
  // UUIDs. Same pattern as `WorkflowsListPage`.
  const orgs = useOrgs();
  const products = useProducts();

  const [orgId, setOrgId] = useState('');
  const [productId, setProductId] = useState('');
  const [status, setStatus] = useState<'' | QuestionSetStatus>('');

  const filters: ListQuestionSetsFilters = {
    orgId: orgId || undefined,
    productId: productId || undefined,
    status: status || undefined,
  };
  const query = useQuestionSets(filters);
  const orgNameById = new Map((orgs.data ?? []).map((o) => [o.id, o.name]));
  const productNameById = new Map((products.data ?? []).map((p) => [p.id, p.displayName]));

  return (
    <Stack gap="lg">
      <Group justify="space-between" align="center">
        <Title order={2}>Question sets</Title>
        <Button component={Link} to="/question-sets/new" leftSection={<IconPlus size={16} />}>
          New question set
        </Button>
      </Group>
      <Text c="dimmed" size="sm" maw={720}>
        Reusable, versioned bundles of questions scoped to an (organisation, product) pair. Workflow{' '}
        <Text component="span" fw={600}>
          collect_questions
        </Text>{' '}
        steps reference a set by id instead of embedding the questions inline, so the same set can
        be used across multiple workflows for one product.
      </Text>

      <Group gap="md" align="flex-end" wrap="wrap">
        <NativeSelect
          label="Organisation"
          value={orgId}
          onChange={(e) => setOrgId(e.currentTarget.value)}
          data={[
            { value: '', label: 'All organisations' },
            ...(orgs.data ?? []).map((o) => ({ value: o.id, label: o.name })),
          ]}
          w={240}
        />
        <NativeSelect
          label="Product"
          value={productId}
          onChange={(e) => setProductId(e.currentTarget.value)}
          data={[
            { value: '', label: 'All products' },
            ...(products.data ?? []).map((p) => ({ value: p.id, label: p.displayName })),
          ]}
          w={240}
        />
        <NativeSelect
          label="Status"
          value={status}
          onChange={(e) => setStatus(e.currentTarget.value as QuestionSetStatus | '')}
          data={[
            { value: '', label: 'Any' },
            { value: 'draft', label: 'Draft' },
            { value: 'active', label: 'Active' },
            { value: 'archived', label: 'Archived' },
          ]}
          w={200}
        />
      </Group>

      {query.isError && (
        <Alert color="red" icon={<IconAlertTriangle size={18} />}>
          Could not load question sets.
        </Alert>
      )}

      {query.isPending ? (
        <Center mih={200}>
          <Text c="dimmed">Loading…</Text>
        </Center>
      ) : (query.data?.items ?? []).length === 0 ? (
        <Center mih={200}>
          <Text c="dimmed">No question sets match these filters.</Text>
        </Center>
      ) : (
        <Table highlightOnHover withTableBorder>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Name</Table.Th>
              <Table.Th>Organisation</Table.Th>
              <Table.Th>Product</Table.Th>
              <Table.Th ta="right">Version</Table.Th>
              <Table.Th ta="right">Questions</Table.Th>
              <Table.Th>Status</Table.Th>
              <Table.Th>Created</Table.Th>
              <Table.Th>Published</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {(query.data?.items ?? []).map((qs) => (
              <Table.Tr key={qs.id}>
                <Table.Td>
                  <Anchor component={Link} to={`/question-sets/${qs.id}`} size="sm">
                    {qs.name}
                  </Anchor>
                  {qs.description && (
                    <Text size="xs" c="dimmed">
                      {qs.description}
                    </Text>
                  )}
                </Table.Td>
                <Table.Td>
                  {qs.orgId === null ? (
                    <Text size="sm" c="dimmed" fs="italic">
                      Default — all orgs
                    </Text>
                  ) : (
                    <Text size="sm">{orgNameById.get(qs.orgId) ?? qs.orgId}</Text>
                  )}
                </Table.Td>
                <Table.Td>
                  <Text size="sm">{productNameById.get(qs.productId) ?? qs.productId}</Text>
                </Table.Td>
                <Table.Td ta="right">v{qs.version}</Table.Td>
                <Table.Td ta="right">{qs.questions.length}</Table.Td>
                <Table.Td>{statusBadge(qs.status)}</Table.Td>
                <Table.Td>
                  <Text size="sm" title={qs.createdAt}>
                    {formatDate(qs.createdAt)}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Text size="sm" title={qs.publishedAt ?? ''}>
                    {formatDate(qs.publishedAt)}
                  </Text>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      )}
    </Stack>
  );
}
